import React, { useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Select, { StylesConfig } from "react-select";
import "bootstrap-daterangepicker/daterangepicker.css";
import { BotFilterOption, FilterOption } from "./chatAnalyticsService";

interface ChatAnalyticsFiltersProps {
  loading: boolean;
  dateRange: [Date | null, Date | null];
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  platformOptions: FilterOption[];
  platformId: number | string | null;
  onPlatformChange: (id: number | string | null) => void;
  channelOptions: FilterOption[];
  channelId: number | string | null;
  onChannelChange: (id: number | string | null) => void;
  salesUserOptions: FilterOption[];
  salesUserId: number | string | null;
  onSalesUserChange: (id: number | string | null) => void;
  teamOptions: FilterOption[];
  teamId: number | string | null;
  onTeamChange: (id: number | string | null) => void;
  botOptions: BotFilterOption[];
  botId: number | string | null;
  onBotChange: (id: number | string | null) => void;
  statusOptions: FilterOption[];
  statusId: number | null;
  onStatusChange: (id: number | null) => void;
  typeOptions: FilterOption[];
  typeId: number | null;
  onTypeChange: (id: number | null) => void;
  onClear?: () => void;
}

const dateRangeSettings = {
  endDate: new Date(),
  ranges: {
    "Last 30 Days": [
      new Date(new Date().setDate(new Date().getDate() - 30)),
      new Date(),
    ],
    "Last 7 Days": [
      new Date(new Date().setDate(new Date().getDate() - 7)),
      new Date(),
    ],
    "This Month": [
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      new Date(),
    ],
    "Last Month": [
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      new Date(new Date().getFullYear(), new Date().getMonth(), 0),
    ],
    Today: [new Date(), new Date()],
  },
  startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
  timePicker: false,
};

const botSelectStyles: StylesConfig<BotFilterOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? "#8b5cf6" : "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#8b5cf6" },
  }),
  valueContainer: (base) => ({ ...base, fontSize: 14 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const ChatAnalyticsFilters: React.FC<ChatAnalyticsFiltersProps> = ({
  loading,
  dateRange,
  onDateRangeChange,
  platformOptions,
  platformId,
  onPlatformChange,
  channelOptions,
  channelId,
  onChannelChange,
  salesUserOptions,
  salesUserId,
  onSalesUserChange,
  teamOptions,
  teamId,
  onTeamChange,
  botOptions,
  botId,
  onBotChange,
  statusOptions,
  statusId,
  onStatusChange,
  typeOptions,
  typeId,
  onTypeChange,
}) => {
  const [expanded, setExpanded] = useState(true);

  const hasActiveFilters =
    !!dateRange[0] ||
    !!dateRange[1] ||
    platformId !== null ||
    channelId !== null ||
    salesUserId !== null ||
    teamId !== null ||
    botId !== null ||
    statusId !== null ||
    typeId !== null;

  const selectedBotOption = botOptions.find((opt) => opt.value === botId) || null;

  return (
    <div className="card mb-4 ca-filters-card">
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between">
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <h6 className="mb-0 d-flex align-items-center">
              <i className="ti ti-filter me-2" />
              Chat Filters
            </h6>
            <i className={`ti ti-chevron-${expanded ? "up" : "down"} ms-2 ca-filters-chevron`} />
          </button>

        </div>
      </div>
      {expanded && (
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6 col-lg-3">
              <label className="form-label">Date Range</label>
              <div className={`icon-form ${dateRange[0] ? "filter-active" : ""}`}>
                <span className="form-icon">
                  <i className="ti ti-calendar" />
                </span>
                <DateRangePicker
                  initialSettings={{ ...dateRangeSettings, autoUpdateInput: false }}
                  onApply={(_event, picker) => {
                    onDateRangeChange(picker.startDate.toDate(), picker.endDate.toDate());
                  }}
                  onCancel={() => onDateRangeChange(null, null)}
                >
                  <input
                    className={`form-control bookingrange table-filters-daterange ${dateRange[0] ? "filter-active-input" : ""
                      }`}
                    type="text"
                    placeholder="All time"
                    readOnly
                    value={
                      dateRange[0] && dateRange[1]
                        ? `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`
                        : ""
                    }
                    onChange={() => { }}
                    style={{ paddingLeft: "34px", cursor: "pointer" }}
                    disabled={loading}
                  />
                </DateRangePicker>
              </div>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Platform</label>
              <select
                className="form-select"
                value={platformId ?? ""}
                onChange={(e) => onPlatformChange(e.target.value ? e.target.value : null)}
                disabled={loading}
              >
                <option value="">All Platforms</option>
                {platformOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Channel</label>
              <select
                className="form-select"
                value={channelId ?? ""}
                onChange={(e) => onChannelChange(e.target.value ? e.target.value : null)}
                disabled={loading}
              >
                <option value="">All Channels</option>
                {channelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Sales User</label>
              <select
                className="form-select"
                value={salesUserId ?? ""}
                onChange={(e) => onSalesUserChange(e.target.value ? e.target.value : null)}
                disabled={loading}
              >
                <option value="">All Sales Users</option>
                {salesUserOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Assigned Team</label>
              <select
                className="form-select"
                value={teamId ?? ""}
                onChange={(e) => onTeamChange(e.target.value ? e.target.value : null)}
                disabled={loading}
              >
                <option value="">All Teams</option>
                {teamOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Bot</label>
              <Select<BotFilterOption, false>
                classNamePrefix="ca-bot-select"
                styles={botSelectStyles}
                isClearable
                isDisabled={loading}
                placeholder="All Bots"
                options={botOptions}
                value={selectedBotOption}
                onChange={(opt) => onBotChange(opt ? opt.value : null)}
                getOptionValue={(opt) => String(opt.value)}
                formatOptionLabel={(opt) => (
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span>{opt.label}</span>
                    <span className={`ca-scope-badge ${opt.isInternal ? "ca-scope-internal" : "ca-scope-external"}`}>
                      {opt.isInternal ? "Internal" : "External"}
                    </span>
                  </div>
                )}
              />
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Chat Status</label>
              <select
                className="form-select"
                value={statusId ?? ""}
                onChange={(e) => onStatusChange(e.target.value ? Number(e.target.value) : null)}
                disabled={loading}
              >
                <option value="">All Statuses</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label">Chat Type</label>
              <select
                className="form-select"
                value={typeId ?? ""}
                onChange={(e) => onTypeChange(e.target.value ? Number(e.target.value) : null)}
                disabled={loading}
              >
                <option value="">All Types</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAnalyticsFilters;
